import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TokenService, TokenPayload } from '../services/tokenService';
import { firestoreService, TokenSession } from '../services/firestoreService';
import { Copy, Plus, Trash2, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  collection,
  query,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';

interface TokenDisplay extends TokenSession {
  formattedExpiry: string;
  hoursRemaining: number;
}

const TokenManagerPage: React.FC = () => {
  const [tokens, setTokens] = useState<TokenDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(firestore, 'token_sessions'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      const formattedTokens: TokenDisplay[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        const expiryMs = data.expiresAt.toMillis();
        const now = Date.now();
        const hoursRemaining = Math.max(0, Math.ceil((expiryMs - now) / (1000 * 60 * 60)));

        return {
          id: doc.id,
          tokenHash: data.tokenHash,
          createdAt: data.createdAt,
          expiresAt: data.expiresAt,
          status: data.status,
          formattedExpiry: new Date(expiryMs).toLocaleString(),
          hoursRemaining,
        };
      });

      setTokens(formattedTokens);
    } catch (err) {
      console.error('Failed to load tokens:', err);
      toast.error('Failed to load tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    try {
      setGeneratingToken(true);
      // Generate ONE token with expiry info
      const tokenPayload = TokenService.generateToken();
      const newToken = tokenPayload.token;

      // Create session in Firestore using the same token
      await firestoreService.createTokenSession(newToken, tokenPayload.expiresAt);

      // Store token in state to display in modal
      setGeneratedToken(newToken);
      toast.success('Token generated! Copy it below.');
      await loadTokens();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate token';
      toast.error(errorMessage);
      console.error('Token generation error:', err);
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleCopyGeneratedToken = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken);
      setCopiedToken(generatedToken);
      toast.success('Token copied to clipboard!');
      setTimeout(() => setCopiedToken(null), 2000);
    }
  };

  const handleDeleteToken = async (tokenId: string) => {
    if (!window.confirm('Are you sure you want to revoke this token?')) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, 'token_sessions', tokenId));
      await loadTokens();
      toast.success('Token revoked');
    } catch (err) {
      console.error('Failed to delete token:', err);
      toast.error('Failed to revoke token');
    }
  };

  const handleCopyTokenHash = (hash: string) => {
    navigator.clipboard.writeText(hash);
    setCopiedToken(hash);
    toast.success('Copied!');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Access Token Manager
        </h1>
        <p className="text-gray-600">
          Generate and manage access tokens for file downloads
        </p>
      </div>

      {generatedToken && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow-md border-2 border-green-300 p-6 mb-8">
          <div className="flex items-start gap-4">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-green-900 mb-3">
                ✅ Token Generated Successfully
              </h3>
              <div className="bg-white p-4 rounded border-2 border-green-300 mb-4">
                <p className="text-xs text-gray-600 mb-2">Your Access Token:</p>
                <p className="font-mono text-lg font-bold text-gray-900 break-all">
                  {generatedToken}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCopyGeneratedToken}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                >
                  <Copy size={16} />
                  {copiedToken === generatedToken ? 'Copied!' : 'Copy Token'}
                </Button>
                <Button
                  onClick={() => setGeneratedToken(null)}
                  variant="outline"
                  className="text-gray-600"
                >
                  Close
                </Button>
              </div>
              <p className="text-xs text-green-700 mt-3">
                ⏰ Token expires 24 hours from first login. Share this token with users who need file access.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <Button
          onClick={handleGenerateToken}
          disabled={generatingToken}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {generatingToken ? (
            <>
              <Loader className="mr-2 animate-spin" size={18} />
              Generating...
            </>
          ) : (
            <>
              <Plus className="mr-2" size={18} />
              Generate New Token
            </>
          )}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-indigo-600" size={32} />
        </div>
      ) : tokens.length === 0 ? (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-6 text-center">
          <p className="text-blue-900">No tokens created yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Hours Left
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tokens.map((token) => {
                  const isExpired = token.status === 'expired' || token.hoursRemaining === 0;
                  return (
                    <tr
                      key={token.id}
                      className={isExpired ? 'bg-gray-50' : 'hover:bg-gray-50'}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {isExpired ? (
                            <>
                              <AlertCircle size={16} className="text-red-500" />
                              <span className="text-sm font-medium text-red-700">Expired</span>
                            </>
                          ) : token.hoursRemaining < 6 ? (
                            <>
                              <AlertCircle size={16} className="text-orange-500" />
                              <span className="text-sm font-medium text-orange-700">Expiring</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle size={16} className="text-green-500" />
                              <span className="text-sm font-medium text-green-700">Active</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {token.createdAt.toDate().toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {token.formattedExpiry}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {token.hoursRemaining}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleCopyTokenHash(token.tokenHash)}
                            className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                            title="Copy token hash"
                          >
                            <Copy size={16} />
                            {copiedToken === token.tokenHash ? 'Copied' : 'Copy'}
                          </button>
                          <button
                            onClick={() => handleDeleteToken(token.id)}
                            className="text-red-600 hover:text-red-700 flex items-center gap-1"
                            title="Revoke token"
                          >
                            <Trash2 size={16} />
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenManagerPage;
